import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService, LogoutRedirectService } from 'src/app/core/services';
import { MenusService } from '../../../menus';
import { debounce } from 'lodash';
import { BrandingService } from '@app/core/services/branding.service';
import { PmaService } from '@app/pma/pma.service';

export const CLAIMS_URL = 'https://filemyclaim.io';

const RESIZE_EVENT = 'resize';

@Component({
    selector: 'app-header-menu',
    templateUrl: './header-menu.component.html',
    styleUrls: ['./header-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class HeaderMenuComponent implements OnDestroy, OnInit {

  public readonly showMenu$ = new Subject();

  curentUrl: string | null = null;
  private menuSub: Subscription;

  public readonly mainMenuItems$ = this.menuService.mainMenuItems$;

  public readonly additionalMenuItems$ = this.menuService.additionalMenuItems$;

  public readonly accountMenuInMainMenu$ = this.menuService.accountMenuInMain$;

  /** FE-196 — gates the drawer's login links; see the template. */
  public readonly isAuthenticated$ = this.authService.isAuthenticated$;

  private readonly resizeEventListener = debounce(() => this.closeMenu(), 150);

  constructor(
    private authService: AuthService,
    private logoutRedirectService: LogoutRedirectService,
    private router: Router,
    private menuService: MenusService,
    private brandingService: BrandingService,
    private pmaService: PmaService,
  ) {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.curentUrl = event.urlAfterRedirects;
        }
      });
  }

  ngOnInit(): void {
    window.addEventListener(RESIZE_EVENT, this.resizeEventListener);
  }

  ngOnDestroy(): void {
    this.menuSub?.unsubscribe();
    window.removeEventListener(RESIZE_EVENT, this.resizeEventListener);
  }

  logout(event: MouseEvent): void {
    event.preventDefault();
    this.logoutRedirectService.logoutToNewPlatform(this.curentUrl);
    this.closeMenu();
  }

  closeMenu(): void {
    this.showMenu$.next(false);
  }

  showMenu(): void {
    this.showMenu$.next(true);
  }

  /**
   * The drawer's "Get a quote" button. FE-196 — it was the one control in the
   * drawer that left it open: every link goes through `handleCustomMenuClick`
   * or `logout`, which both close, but this went straight to navigation. On a
   * phone that left the drawer covering the page it had just navigated to.
   *
   * Closed FIRST so the Amtrust branch is covered too — that one hands off to
   * an external redirect, and anything after it may never run.
   */
  navigateTo(): void {
    this.closeMenu();
    if (this.brandingService.getIsAmtrustClientSnapshot()) {
      this.pmaService.navigateToAmtrust();
    } else {
      this.router.navigateByUrl('/');
    }
  }

  handleCustomMenuClick(event: MouseEvent, url: string): void {
    event.preventDefault();
    if (url.startsWith('http') || url.startsWith('https')) {
      window.open(url, '_blank').focus();
    } else {
      this.router.navigateByUrl(url);
    }
    this.closeMenu();
  }

  onDropMenuClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.drop-menu-content')) {
      this.closeMenu();
    }
  }
}
